<template>
  <v-card class="payout-items-table">
    <v-card-title class="toolbar white--text">
      <h2>Payout Items ({{ filteredItems.length }} of {{ pagination.totalItems }})</h2>
      <v-spacer />
      <v-select
        v-model="search"
        primary
        dark
        solo-inverted
        flat
        append-icon="pending_actions"
        class="ma-0 mr-4"
        :items="payoutStatus"
        label="Payout Status"
        item-value="value"
        hide-details
        clearable
      />
      <v-text-field
        v-model="search"
        primary
        dark
        solo-inverted
        flat
        append-icon="search"
        label="Search payouts by user, snr, isin..."
        single-line
        hide-details
      />
    </v-card-title>

    <v-card-title class="filters grey lighten-5">
      <div class="filters__header">
        Filtered results ({{ filteredResults.length }} of {{ filteredItems.length }})
        <v-tooltip
          v-if="!hasResultFilters"
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="secondary"
              v-on="on"
            >
              <v-icon>
                filter_list
              </v-icon>
            </v-btn>
          </template>
          <span>Filters</span>
        </v-tooltip>
        <v-tooltip
          v-else
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="primary"
              v-on="on"
              @click="resetResultsFilters"
            >
              <v-icon>
                restore
              </v-icon>
            </v-btn>
          </template>
          <span>Reset filters</span>
        </v-tooltip>
      </div>
      <v-select
        v-model="resultFilters.payoutStatus"

        class="ml-5"
        primary
        :items="payoutStatus"
        label="Status"
        item-value="value"
        clearable
      />
      <v-text-field
        v-model="resultFilters.searchResults"

        class="ml-5"
        append-icon="search"
        label="filter results"
      />
    </v-card-title>

    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredResults"
      :total-items.sync="payoutItemsPagination.total"
      :rows-per-page-items="rowsPerPageItems"
      :loading="loading"
      :expand="expand"
      item-key="id"
      class="elevation-1"
      :pagination.sync="pagination"
      @update:pagination="updatePagination"
    >
      <template v-slot:items="props">
        <tr
          :active="props.selected"
          @click="props.expanded = !props.expanded"
        >
          <td class="text-xs-center">
            {{ props.item.id }}
          </td>
          <td class="text-xs-center">
            <span v-if="props.item.account">
              {{ props.item.account.user_id }}
            </span>
            <v-tooltip
              v-else
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="error"
                  flat
                  icon
                  v-on="on"
                >
                  <v-icon>error</v-icon>
                </v-btn>
              </template>
              <span>Error getting account information</span>
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <span v-if="props.item.account">
              {{ props.item.account.snr }}
            </span>
            <v-tooltip
              v-else
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="error"
                  flat
                  icon
                  v-on="on"
                >
                  <v-icon>error</v-icon>
                </v-btn>
              </template>
              <span>Error getting account information</span>
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            {{ props.item.amount }} €
          </td>
          <td class="text-xs-center">
            {{ props.item.payout.status }}
          </td>
          <td class="text-xs-center">
            {{ props.item.payout.upload_date ?
              formatDateSmall(props.item.payout.upload_date) : null }}
          </td>
          <td class="text-xs-center">
            {{ props.item.automatic_payout }}
          </td>
          <td class="text-xs-center">
            {{ props.item.success }}
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ props.item.payout.created_at ?
                      formatDateSmall(props.item.payout.created_at) : null }}
                  </span>
                </v-chip>
              </template>
              {{ props.item.payout.created_at ?
                formatDate(props.item.payout.created_at) : null }}
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ props.item.payout.updated_at ?
                      formatDateSmall(props.item.payout.updated_at) : null }}
                  </span>
                </v-chip>
              </template>
              {{ props.item.payout.updated_at ?
                formatDate(props.item.payout.updated_at) : null }}
            </v-tooltip>
          </td>
          <td>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="red"
                  flat
                  icon
                  v-on="on"
                  @click.stop="deleteItem(props.item)"
                >
                  <v-icon>delete_outline</v-icon>
                </v-btn>
              </template>
              <span>delete</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
      <template v-slot:expand="props">
        <BaaderRowDetail
          :item="props.item"
          :user-id="props.item.account ? props.item.account.user_id : 0"
          :snr="props.item.account ? props.item.account.snr : 0"
        />
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapActions } from 'vuex';
import { formatDate, formatDateSmall } from '../../utils/dateFormats';
import { toQueryString, setQuery } from '@/helpers';
import PayoutStatusEnum from '@/enum/baader/PayoutStatus';
import BaaderRowDetail from './shared/BaaderRowDetail';

export default {
  name: 'PayoutItemsTable',
  components: {
    BaaderRowDetail,
  },
  props: {
    payoutItems: {
      type: Array,
      default: () => [],
    },
    payoutItemsPagination: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expand: false,
      isLoadTableData: false,
      search: undefined,
      selected: [],
      pagination: {
        page: this.payoutItemsPagination.page,
        rowsPerPage: this.payoutItemsPagination.limit,
        sortBy: 'created_at',
        descending: true,
      },
      rowsPerPageItems: [
        {
          text: '10',
          value: 10,
        },
        {
          text: '25',
          value: 25,
        },
        {
          text: '50',
          value: 50,
        },
        {
          text: '100',
          value: 100,
        },
      ],
      filters: {
        createdAt: [],
        state: [],
      },
      resultFilters: {
        payoutStatus: null,
        searchResults: null,
      },
      headers: [
        {
          text: 'Id',
          align: 'left',
          value: 'id',
        },
        {
          text: 'UserId',
          align: 'left',
          value: 'account.user_id',
        },
        {
          text: 'Snr',
          align: 'left',
          value: 'snr',
        },
        {
          text: 'Amount',
          align: 'left',
          value: 'amount',
        },
        {
          text: 'Payout Status',
          align: 'left',
          value: 'payout.status',
        },
        {
          text: 'Upload Date',
          align: 'left',
          value: 'payout.upload_date',
        },
        {
          text: 'AutomaticPayout',
          align: 'left',
          value: 'automatic_payout',
        },
        {
          text: 'Success',
          align: 'left',
          value: 'success',
        },
        {
          text: 'Created at',
          value: 'created_at',
        },
        {
          text: 'Updated at',
          value: 'updated_at',
        },
        {
          text: 'Actions',
          value: 'actions',
        },
      ],
      formatDateSmall,
      formatDate,
    };
  },
  computed: {
    filteredItems() {
      return this.payoutItems.filter(d => Object.keys(this.filters).every((f) => {
        if (this.filters[f]) {
          return (
            this.filters[f].length < 1 ||
            String(this.filters[f]).includes(d[f])
          );
        }

        return true;
      }));
    },
    filteredResults() {
      if (this.hasResultFilters) {
        return this.filteredItems.filter((item) => {
          let hasStatus = false;
          let hasSearch = false;

          const { payoutStatus, searchResults } = this.resultFilters;

          if (payoutStatus) {
            hasStatus = item.payout.status === payoutStatus;
          }

          if (searchResults) {
            hasSearch = JSON.stringify(item)
              .toLowerCase()
              .includes(searchResults.toLowerCase());
          }

          return (!!hasStatus || !!hasSearch) || false;
        });
      }

      return this.filteredItems;
    },

    payoutStatus() {
      return Object.keys(PayoutStatusEnum).map(key => ({
        text: PayoutStatusEnum[key],
        value: PayoutStatusEnum[key],
      }));
    },
    hasResultFilters() {
      return !!this.resultFilters.payoutStatus
        || !!this.resultFilters.searchResults;
    },
  },
  watch: {
    async payoutItems() {
      this.getDefaultFilters();
      await this.$nextTick();
      await this.changePage();
    },
    payoutItemsPagination: {
      immediate: false,
      async handler(payoutItemsPagination) {
        if (payoutItemsPagination) {
          this.pagination.page = payoutItemsPagination.page;
          this.pagination.rowsPerPage = payoutItemsPagination.limit;
          this.pagination.totalItems = payoutItemsPagination.total;
        }
      },
    },
    search: {
      immediate: false,
      handler(search, oldSearch) {
        if (search !== oldSearch) {
          setQuery(
            {
              search,
            },
            this.$route,
            this.$router,
          );
          this.refreshTable();
        }
      },
    },
    pagination: {
      immediate: false,
      handler(pagination, oldPagination) {
        if (!_.isEqual({
          descending: pagination.descending,
          page: pagination.page,
          rowsPerPage: pagination.rowsPerPage,
          sortBy: pagination.sortBy,
          totalItems: pagination.totalItems,
        }, {
          descending: oldPagination.descending,
          page: oldPagination.page,
          rowsPerPage: oldPagination.rowsPerPage,
          sortBy: oldPagination.sortBy,
          totalItems: oldPagination.totalItems || 0,
        })) {
          if (pagination.sortBy) {
            this.refreshTable();
          }
        }
      },
    },
  },
  methods: {
    ...mapActions([
      'getPayoutItems',
      'deletePayoutItem',
    ]),
    refreshTable() {
      const query = toQueryString({
        page: this.pagination.page || 1,
        limit: this.pagination.rowsPerPage || 25,
        search: this.search,
        sort: this.pagination.sortBy,
        dir: this.pagination.descending ? 'desc' : 'asc',
      });
      if (typeof window.LIT !== 'undefined') {
        clearTimeout(window.LIT);
      }
      window.LIT = setTimeout(() => {
        this.getPayoutItems(query);
      }, 1000);
    },
    updatePagination(pagination) {
      setQuery(
        {
          page: pagination.page,
          sortBy: pagination.sortBy,
          dir: pagination.descending ? 'desc' : 'asc',
          search: this.search,
        },
        this.$route,
        this.$router,
      );
    },
    async changePage() {
      const { page, search } = this.$route.query;
      this.search = search;
      await this.$nextTick();
      this.isLoadTableData = true;
      this.pagination.page = page ? Number(page) : 1;
    },
    getDefaultFilters() {
      if (this.$route.query) {
        Object.keys(this.$route.query).forEach((item) => {
          let value = this.$route.query[item];

          if (Object.prototype.hasOwnProperty.call(this.filters, item)) {
            if (typeof this.filters[item] === 'string') {
              this.filters[item] = value;
            } else if (String(this.$route.query[item]).includes(';')) {
              value = this.$route.query[item].split(';');
              this.filters[item] = value;
            } else {
              this.filters[item].push(value);
            }
          }
        });
      }
    },
    resetResultsFilters() {
      this.resultFilters = _.mapValues(this.resultFilters, () => null);
    },
    deleteItem(payoutItem) {
      this.deletePayoutItem(payoutItem.id)
        .then(this.refreshTable);
    },
  },
};
</script>

<style lang="scss" scoped>
.payout-items-table{
  a {
    text-decoration: none;
    color: black;
  }

  .toolbar {
    background-color: #1a4c7d;
    color: white;
  }

  .infoLabel {
    margin-left: 16px;
  }

  .countLabel {
    color: #fff;
  }
  .expanded-details {
    background-color: #eaeaea;
    padding: 20px;
  }

  .v-select{
        flex: 1;
  }

  table.v-table {
    td:not(:first-child),
    th:not(:first-child) {
      padding: 0 1% !important;
    }
  }

  .v-card__title.subheading{
    .v-icon{
      margin-right:10px;
    }
  }
  .v-input{
    &.security_wallet{
      min-width: 350px;
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/components/baader/PayoutItemsTable.vue